/**
 * Centralized Z-Index System
 * 
 * Use these constants to maintain consistent z-index hierarchy across the app.
 * This prevents dialogs from appearing behind other elements.
 * 
 * @example
 * ```tsx
 * // For a basic dialog
 * <DialogContent className={`z-[${Z_INDEX.BASE_DIALOG}]`}>
 * 
 * // For a dialog opened from another dialog
 * <NestedDialogContent className={`z-[${Z_INDEX.NESTED_DIALOG}]`}>
 * ```
 */
export const Z_INDEX = {
  /** Base dialog layer (z-50) - Used by standard DialogContent */
  BASE_DIALOG: 50,
  
  /** Mobile/fullscreen dialog layer (z-60) - Used by MobileDialogContent */
  MOBILE_DIALOG: 60,
  
  /** Nested dialog layer (z-70/80) - Used by NestedDialogContent when opened from another dialog */
  NESTED_DIALOG: 70,
  NESTED_DIALOG_OVERLAY: 60,
  
  /** Deep nested dialog layer (z-80/90) - Used when you need 3+ levels of dialogs */
  DEEP_NESTED_DIALOG: 90,
  DEEP_NESTED_DIALOG_OVERLAY: 80,
  
  /** Custom modals (z-60) - For non-Radix UI modals like InterviewBookingModal */
  CUSTOM_MODAL: 60,
  
  /** Mobile header bar (z-40) - Above page content but below dialogs/menus */
  MOBILE_HEADER: 40,
  
  /** Sheet/Drawer layer (z-45/46) - Mobile menu, above header but below dialogs */
  SHEET_OVERLAY: 45,
  SHEET_CONTENT: 46,
  
  /** Dropdown/Popover/Select layer (z-200) - Must be above all dialogs to remain interactive */
  DROPDOWN: 200,
  POPOVER: 200,
  SELECT: 200,
  
  /** Interview Mode - Above all platform UI */
  INTERVIEW_CONTAINER: 9000,
  INTERVIEW_MASK: 8999,
  INTERVIEW_CONTENT: 9001,
} as const;

/**
 * Dialog Nesting Rules:
 * 
 * 1. **Base Dialog** (z-50): Use `DialogContent` for standalone dialogs
 * 2. **Mobile Dialog** (z-60): Use `MobileDialogContent` for fullscreen mobile dialogs
 * 3. **Nested Dialog** (z-70): Use `NestedDialogContent` when opening from another dialog
 * 4. **Deep Nested** (z-90): Use `DeepNestedDialogContent` for 3+ levels (rare)
 * 
 * Examples:
 * - JobApplicationDialog (z-60) → UserProfileDialog (z-70) ✅
 * - CandidateDetailDialog (z-60) → InterviewBookingModal (z-70) ✅
 * - Dialog A (z-50) → Dialog B (z-70) → Dialog C (z-90) ✅
 */
